/**
 * `ClaimFilterAudience` — DE ENE GEDEELDE DEFINITIE van "mag deze gebruiker het
 * claimfilter-beheerscherm zien?" (story `fe-be-claimfilter-beheerscherm`,
 * hoofdstuk 4.4, AC-4 t/m AC-7 en AC-40).
 *
 * Hij beantwoordt precies één vraag, met precies één regel:
 *
 *     mag = (contexttype is System)  OF  (de gebruiker draagt de support-rol)
 *
 * **OF, niet EN.** Dat is de kern, en het is precies de val die de story
 * beschrijft: de bestaande poortvelden in `apps.config.ts` componeren met **EN**
 * (`AppSwitcher`: `(!app.requiresSystemContext || isSystemUser) && (!app.requiresAnyRole || …)`).
 * Wie op de claimfilter-app zowel `requiresSystemContext: true` als
 * `requiresAnyRole: ['helpdesk']` zet, krijgt een tegel die alleen zichtbaar is
 * voor iemand die system-context **én** de helpdesk-rol heeft — dus voor
 * niemand. Er komt geen foutmelding uit; er verschijnt alleen nooit een tegel,
 * en dat is van buiten niet te onderscheiden van "nog niet gebouwd".
 *
 * ## Waarom dit een eigen module is en geen `if` in de switcher
 *
 * De FE bepaalt of de **tegel zichtbaar** is; de BE bepaalt of een **endpoint
 * antwoordt**. Keyen die twee op verschillende dingen — de BE op contexttype
 * `System`, de FE op contexttype **óf** rol — dan ziet een helpdesk-gebruiker een
 * tegel die op elke route **403** geeft. Een tegel die alleen fouten oplevert is
 * van buiten niet te onderscheiden van "de rol is aan niemand toegekend" (R-8),
 * en dat is de valse-groene vorm die dit hele scherm bestrijdt, dan toegepast op
 * de story zelf.
 *
 * Daarom: één definitie, hier. De BE-tegenhanger staat in
 * `C:\CARculate\AppServices\ClaimFilter\ClaimFilterAudience.cs` met **dezelfde
 * twee termen en dezelfde OF**, en de beslistabel DT-4 wordt aan beide zijden
 * over dezelfde zeven regels gedraaid (AC-40).
 *
 * ## De rol-term (M-1 / O-0 — beantwoord op 2026-08-31)
 *
 * Gemeten in de database:
 * - module **`HD`** bestaat in `GF_Module`, `Description = "Support"`;
 * - `WF_Role` **`Helpdesk`** bestaat en hangt onder de `ModuleID` van `HD` —
 *   exact dezelfde vorm als `GlasAdmin` en `Hoofdkantoor`, die onder module `UA`
 *   (Glas) hangen;
 * - **44** gebruikers dragen die rol via `WF_UserRole`.
 *
 * ⚠ De poort staat op de **ROL**, niet op de module-toegang. **1.772** gebruikers
 * hebben module-toegang tot `HD` via `GF_ModuleAccess`; dat is veel te ruim voor
 * een beheerscherm. Beslissing van de gebruiker (2026-08-31): **rol `Helpdesk`
 * óf `system`, verder niemand.**
 *
 * De claim-string is **lowercase** (`'helpdesk'`), conform de bestaande
 * `glasadmin`/`hoofdkantoor`/`verzekeraar`-keuze: `Array.includes` is
 * case-sensitive aan de FE-kant, terwijl de BE-`HasRole` case-insensitief is —
 * één canonieke lowercase spelling werkt daarmee aan beide kanten.
 *
 * ## Nergens anders een inline vergelijking
 *
 * Spiegel van `@/auth/restrictedRepairer`: deze module is puur, React-vrij en
 * heeft geen module-state, en er staat een scan-test op
 * (`claimFilterAudience.scan.test.ts`) die rood wordt zodra elders in
 * `apps/web/` een inline `'helpdesk'`-vergelijking of een tweede
 * `userContextType === 'System' || …`-poort voor dit scherm opduikt.
 */
import type { WhoAmIResponse } from '@carculate/api-client';

/**
 * De canonieke claim-string van de support-rol. **Lowercase** — zie de
 * module-doc. Dit is de ENIGE plek in `apps/web/` waar deze string voorkomt.
 */
export const CLAIMFILTER_SUPPORT_ROLE = 'helpdesk';

/**
 * De rol-term van de poort, als lijst — zodat hij één-op-één in het
 * declaratieve `App.requiresSystemContextOrAnyRole`-veld past en er later een
 * tweede rol bij kan zonder dat de poortlogica verandert.
 */
export const CLAIMFILTER_AUDIENCE_ROLES: readonly string[] = [CLAIMFILTER_SUPPORT_ROLE];

/**
 * De route-prefixen van de app — de **vijf** subschermen uit het concept.
 *
 * ⚠ `claimFilters` staat **tussen** `dossiers` en `health`, niet erachter:
 * Overzicht/Rondes/Dossiers/Claimfilters gaan over het werk, Gezondheid is de
 * staande controle en hoort als laatste (story §4.1).
 *
 * ⚠ Dit is de ENIGE plek waar deze paden als literal staan — `apps.config.ts`
 * en `routes.tsx` lezen ze hier.
 */
export const CLAIMFILTER_ROUTES = {
    overview: '/claimfilter',
    rounds: '/claimfilter/rondes',
    dossiers: '/claimfilter/dossiers',
    claimFilters: '/claimfilter/claimfilters',
    /**
     * De oordeelsregels (story `fe-be-claimfilter-oordeelsregels-beheer`, 04-09-2026).
     *
     * ⚠⚠ **DIT IS DE ENIGE ROUTE HIER DIE NIET ONDER `/claimfilter/*` HANGT, en dat is
     * geen slordigheid maar de kern van de verhuizing van 09-09-2026.** Die prefix draagt
     * de system/support-poort van de app; de verzekeraar zou daar STIL op stuklopen — de
     * `InsurerRouteGuard` stuurt haar dan zonder melding terug naar "Te beoordelen" en het
     * lijkt alsof de link kapot is, terwijl het endpoint haar netjes kent.
     *
     * Op 04-09 stond hij nog wél onder die prefix, want toen was de verzekeraar bewust
     * buitengesloten (*"Hou de verzekeraar nog even buiten beschouwing"*). Op 09-09 kwam
     * zij erbij en is hij top-level gemaakt — precies zoals de aantekening in
     * `InsurerRouteGuard.tsx` toen voorschreef.
     *
     * ⚠ **Twee publieken, één scherm.** System/support bereiken hem via de zesde tab van
     * de Claimfilter-app; de verzekeraar via haar eigen Claimfilter-tegel. Het BACKEND
     * bepaalt wat ieder ziet: `ResolveInsurerIds()` geeft system/support alle
     * maatschappijen en de verzekeraar uitsluitend die van haar eigen bedrijf.
     *
     * ⚠ Hij blijft in dit object staan omdat dit de enige plek is waar de claimfilter-paden
     * als literal voorkomen — ook nu de zijne buiten de prefix valt.
     */
    rules: '/oordeelsregels',
    health: '/claimfilter/gezondheid',
} as const;

/**
 * Het detail van één claimfilter.
 *
 * ⚠ De sleutel is `CF_ClaimFilter.Number` en **niet** de GUID (story §5.7.1
 * punt 1): dat is wat een mens in de lijst ziet, wat in de URL leesbaar blijft
 * en wat een support-medewerker aan de telefoon voorleest.
 */
export function claimFilterDetailRoute(number: number | string): string {
    return `${CLAIMFILTER_ROUTES.claimFilters}/${number}`;
}

/**
 * De doorklik **dossier → claimfilters** (AC-53).
 *
 * ⚠ Levert een **gefilterde lijst** en nooit een sprong naar de eerste: een
 * dossier kan er meerdere hebben (202605465 heeft er twee). De brug loopt over
 * `UA_DossierClaimFilter.ClaimFilterID` — de `CF_`-kop kent het dossier niet.
 */
export function claimFiltersForDossierRoute(dossierId: string): string {
    return `${CLAIMFILTER_ROUTES.claimFilters}?dossierId=${encodeURIComponent(dossierId)}`;
}

/**
 * De doorklik **ronde → claimfilters** (AC-54).
 *
 * ⚠⚠ Dit is een **tijdvak-filter**, geen vastgelegde koppeling. Er bestaat geen
 * verwijzing van een ronde naar een claimfilter: de gebeurtenis-tabel draagt
 * alleen een `DossierID`, en juist de kandidaten die wél een claimfilter
 * opleverden laten per ontwerp géén gebeurtenis-rij na. Het scherm labelt de
 * uitkomst daarom als *"claimfilters uit het tijdvak van deze ronde"* en nooit
 * als *"de claimfilters van deze ronde"*.
 */
export function claimFiltersForRoundRoute(startedAt: string, finishedAt: string): string {
    const params = new URLSearchParams({ from: startedAt, to: finishedAt });
    return `${CLAIMFILTER_ROUTES.claimFilters}?${params.toString()}`;
}

/**
 * De generieke OF-poort achter het declaratieve veld
 * `App.requiresSystemContextOrAnyRole` (AC-5.1).
 *
 * Levert `true` wanneer de gebruiker **System**-context heeft **OF** minstens
 * één van `roles` draagt. Bewust generiek (en niet claimfilter-specifiek), zodat
 * `AppSwitcher`, `AppTabs` én `ProtectedRoute` alle drie hetzelfde veld kunnen
 * lezen zonder een `app.id === 'claimfilter'`-vergelijking — die is door AC-5.1
 * expliciet verboden.
 *
 * Fail-closed: geen user, of een lege/ontbrekende rollenlijst zonder
 * System-context → `false`.
 *
 * @param user  De whoami-respons (mag `null`/`undefined` zijn).
 * @param roles De rol-term van de poort. `undefined`/leeg → alleen de
 *              System-term telt nog.
 */
export function maySeeWithSystemOrAnyRole(
    user: WhoAmIResponse | null | undefined,
    roles: readonly string[] | undefined,
): boolean {
    if (!user) return false;

    // Term 1 — het contexttype.
    if (user.userContextType === 'System') return true;

    // Term 2 — de rol. OF, niet EN: we vallen hier alleen doorheen wanneer de
    // eerste term niet gold.
    if (!roles || roles.length === 0) return false;
    const userRoles = Array.isArray(user.roles) ? user.roles : [];
    return roles.some((role) => userRoles.includes(role));
}

/**
 * Mag deze gebruiker het claimfilter-beheerscherm zien? De concrete toepassing
 * van {@link maySeeWithSystemOrAnyRole} op {@link CLAIMFILTER_AUDIENCE_ROLES}.
 *
 * Dit is de functie waarop de FE-tests van DT-4 draaien; de tegel-, tab- en
 * route-poort lezen alle drie hetzelfde declaratieve veld dat met deze
 * rollenlijst gevuld is.
 */
export function mayViewClaimFilter(user: WhoAmIResponse | null | undefined): boolean {
    return maySeeWithSystemOrAnyRole(user, CLAIMFILTER_AUDIENCE_ROLES);
}
